<template>
  <div class="d-flex flex-column m-dashboard-widget__col-3 m-dashboard-widget__row-2">
    <div class="d-flex justify-space-between">
      <h4>{{ $t('dashboard.widgets.user_matches.title') }}</h4>
      <div class="d-flex align-center">
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-btn v-if="matches.length" icon v-bind="attrs" @click="download" v-on="on">
              <v-icon>mdi-download</v-icon>
            </v-btn>
          </template>
          <span>{{ $t('dashboard.widgets.user_matches.downloadTooltip') }}</span>
        </v-tooltip>
      </div>
    </div>
    <div class="flex-grow-1 overflow-auto scroll-wrapper">
      <v-simple-table v-if="matches.length > 0" fixed-header height="280px">
        <thead>
          <tr class="text-left valign-top">
            <th v-for="(header, index) in headers" :key="`header-${index}}`">
              {{ $t(`dashboard.widgets.user_matches.table.header.${header}`) }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(match, index) in matches" :key="`row-${index}`">
            <td v-for="(header, index) in headers" :key="`column-${index}}`">
              <span v-if="header === 'user'">
                <v-progress-circular v-if="loadingUsers && !match.userObj" size="20" indeterminate />
                <m-user-preview v-else-if="match.userObj" :user="match.userObj" />
                <span v-else>User #{{ match.id }}</span>
              </span>
              <span v-else-if="header === 'matches'">
                {{ match.count }}
              </span>
              <span v-else>{{ getValue(match[header]) }}</span>
            </td>
          </tr>
          <tr v-if="matches.length > 0">
            <td v-for="(header, index) in headers" :key="`total-${index}}`" :class="{ 'text-end': header === 'user' }">
              <span v-if="header === 'user'">
                <strong>{{ $t(`dashboard.widgets.user_matches.table.total`) }}</strong>
              </span>
              <span v-else-if="header === 'matches'">
                <strong>{{ totalMatches }}</strong>
              </span>
            </td>
          </tr>
        </tbody>
      </v-simple-table>
      <div v-else-if="error" class="d-flex justify-center align-center fill-height">
        <div class="text-center">
          <v-icon color="error" class="mb-2">mdi-alert-circle</v-icon>
          <div class="red--text">Error loading data</div>
          <small class="grey--text">{{ error }}</small>
        </div>
      </div>
      <div v-else-if="loadingUsers" class="d-flex justify-center align-center fill-height">
        <v-progress-circular indeterminate />
        <span class="ml-2">Loading user details...</span>
      </div>
      <div v-else class="d-flex justify-center align-center fill-height">
        <span class="grey--text">{{ $t('dashboard.widgets.user_matches.noData') }}</span>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue';
import { MUserPreview } from '@/components/MUserPreview';
import { IUser } from '@mentessa/types';
import { UsersActions } from '@/store/users/types';

interface UserMatchData {
  user_matches?: Array<UserMatchRow>;
}

interface UserMatchRow {
  id: number;
  count: string;
  user?: number; // For backward compatibility
  matches?: string; // For backward compatibility
  userObj?: IUser;
}

export default Vue.extend({
  name: 'MUserMatchesWidget',
  components: {
    MUserPreview,
  },
  props: {
    value: { type: Object as PropType<UserMatchData> },
    settings: { type: Object, default: () => ({}) },
  },
  data: () => ({
    enhancedMatches: [] as Array<UserMatchRow>,
    loadingUsers: true,
    error: null as string | null,
  }),
  async mounted() {
    console.log('MUserMatchesWidget mounted, value prop:', this.value);

    // Check if there's an error in the data
    if (this.value?.user_matches?.error) {
      console.error('Backend error detected:', this.value.user_matches.error);
      this.error = this.value.user_matches.error.message || 'Unknown backend error';
      this.loadingUsers = false;
      return;
    }

    if (this.value?.user_matches?.length) {
      console.log('Found user matches data, loading user details...');
      await this.loadUserDetails();
    } else {
      console.log('No user matches data found:', this.value);
      this.loadingUsers = false;
    }
  },
  computed: {
    headers() {
      // Use columns from settings if available, otherwise default
      return this.settings?.columns || ['user', 'matches'];
    },
    matches(): Array<UserMatchRow> {
      console.log('MUserMatchesWidget - value prop:', this.value);
      console.log('MUserMatchesWidget - enhancedMatches:', this.enhancedMatches);

      // If there's an error, return empty array
      if (this.error || this.value?.user_matches?.error) {
        console.log('MUserMatchesWidget - returning empty array due to error');
        return [];
      }

      // Return enhanced matches if available, otherwise raw data
      if (this.enhancedMatches.length > 0) {
        return this.enhancedMatches;
      }

      const result = this.value?.user_matches ?? [];
      console.log('MUserMatchesWidget - raw matches data:', result);

      // Make sure result is an array and has valid data
      if (Array.isArray(result)) {
        return result;
      }

      console.warn('MUserMatchesWidget - user_matches is not an array:', result);
      return [];
    },
    totalMatches(): number {
      return this.matches.reduce((total, match) => {
        const matchCount = match.count || match.matches || '0';
        return total + parseInt(matchCount, 10);
      }, 0);
    },
  },
  methods: {
    async loadUserDetails() {
      try {
        this.loadingUsers = true;
        const matches = this.value?.user_matches || [];

        // Validate that matches is an array and contains valid data
        if (!Array.isArray(matches) || matches.length === 0) {
          console.warn('loadUserDetails: No valid matches array found');
          this.enhancedMatches = [];
          return;
        }

        // Load user details for each unique user ID
        const userPromises = matches.map(async (match) => {
          try {
            // Validate that match has a valid user ID
            if (!match.id || match.id === undefined || match.id === null) {
              console.warn('loadUserDetails: Invalid user ID:', match);
              return {
                ...match,
                userObj: {
                  id: match.id || 'unknown',
                  identity: {
                    attributes: {
                      email: '',
                      firstName: `Invalid User`,
                      lastName: '',
                    },
                  },
                } as IUser,
              };
            }

            const user = await this.$store.dispatch(`users/${UsersActions.LoadUserDetails}`, {
              userId: match.id, // Use 'id' instead of 'user' based on the API response
              fetchOnly: true,
            });
            return {
              ...match,
              user: match.id, // Ensure backward compatibility
              userObj: user,
            };
          } catch (error) {
            console.error(`Failed to load user ${match.id}:`, error);
            // Fallback to basic user object if loading fails
            return {
              ...match,
              user: match.id,
              userObj: {
                id: match.id,
                identity: {
                  attributes: {
                    email: '',
                    firstName: `User ${match.id}`,
                    lastName: '',
                  },
                },
              } as IUser,
            };
          }
        });

        this.enhancedMatches = await Promise.all(userPromises);
      } catch (error) {
        console.error('Error loading user details:', error);
        // Fallback to basic user objects only if we have valid array data
        const matches = this.value?.user_matches || [];
        if (Array.isArray(matches)) {
          this.enhancedMatches = matches.map((match) => ({
            ...match,
            user: match.id,
            userObj: {
              id: match.id,
              identity: {
                attributes: {
                  email: '',
                  firstName: `User ${match.id}`,
                  lastName: '',
                },
              },
            } as IUser,
          }));
        } else {
          this.enhancedMatches = [];
        }
      } finally {
        this.loadingUsers = false;
      }
    },
    download() {
      const csvContent = [
        'data:text/csv;charset=utf-8,',
        [
          this.$t('dashboard.widgets.user_matches.table.header.user'),
          this.$t('dashboard.widgets.user_matches.table.header.matches'),
        ].join(','),
        ...this.matches.map((match) => {
          const userAttrs = match.userObj?.identity?.attributes;
          const user =
            userAttrs?.firstName && userAttrs?.lastName
              ? `${userAttrs.firstName} ${userAttrs.lastName}`
              : userAttrs?.firstName || userAttrs?.email || `User #${match.user}`;
          const matches = match.matches;
          return `${user},${matches}`;
        }),
      ].join('\n');
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement('a');
      link.setAttribute('href', encodedUri);
      link.setAttribute('download', 'user_matches.csv');
      document.body.appendChild(link);
      link.click();
    },
    getValue(value: string | number | boolean | undefined) {
      if (typeof value === 'boolean') {
        return value ? 'Yes' : 'No';
      }
      return value ?? '';
    },
  },
});
</script>

<style lang="scss" scoped>
.scroll-wrapper {
  overflow: auto;
}
</style>
